import { useMemo } from 'react';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { Dropdown, DropdownIcon } from 'framework/dropdown';
import { useGridContext } from 'framework/Grid/context/GridContext';
import { useGridRowContext } from 'framework/Grid/context/GridRowContext';

import { EMPTY_ARRAY, GRID_ROW_BORDER } from '../../../constants';
import { EMPTY_FUNCTION } from '../../../constants';
import { Input, InputCurrency, InputNumber, InputPercent } from '../../Input';
import { useGridColumnContext } from '../context/GridColumnContext';

import {
  GridCellIconSelectProps,
  GridCellInputProps,
  GridCellProps,
  GridCellSelectProps,
  GridFieldCellProps,
} from './types';

import cn from './GridCell.module.scss';

export const GridFieldCell = <T extends Record<string, any>>({
  field,
  text_align,
  getValue,
}: GridFieldCellProps<T>) => {
  const { row } = useGridRowContext<T>();
  const { column } = useGridColumnContext();

  const value = useMemo(() => {
    if (field) {
      return row[field];
    }
    if (getValue) {
      return getValue(row);
    }
    return null;
  }, [getValue, row, field]);

  return (
    <div className={cn.cell} style={{ justifyContent: text_align || column.text_align }}>
      <span>{value}</span>
    </div>
  );
};

export const GridCell = ({
  children,
  background,
  text_align,
  gap = '4px',
  padding = '0px',
}: GridCellProps) => {
  const { column } = useGridColumnContext();

  return (
    <div
      className={cn.cell}
      style={{
        gap,
        padding,
        justifyContent: text_align || column.text_align,
        background,
      }}
    >
      {children}
    </div>
  );
};

export const GridCellIconSelect = <T extends Record<string, any>, O extends Record<string, any>>({
  icon = faEllipsisV,
  color,
  background,
  padding = '0px',
  options = EMPTY_ARRAY,
  loading = false,
  error = false,
  option_key,
  onSelect = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  disabled = false,
}: GridCellIconSelectProps<T, O>) => {
  const { row_height } = useGridContext();
  const { row } = useGridRowContext<T>();

  return (
    <div
      className={classNames(cn.cell, cn.icon)}
      style={{ padding, background, color, width: '100%' }}
      onClick={() => onClick(row)}
    >
      <DropdownIcon
        icon={icon}
        width="100%"
        height={`${row_height - GRID_ROW_BORDER}px`}
        background={background}
        loading={loading}
        error={error}
        onSelect={(option: O) => onSelect(option, row)}
        options={options}
        option_key={option_key}
        disabled={disabled}
        transparent
      />
    </div>
  );
};

export const GridCellSelect = <T extends Record<string, any>, O extends Record<string, any>>({
  left_icon,
  color,
  background,
  padding = '5px',
  options = EMPTY_ARRAY,
  value = '',
  loading = false,
  placeholder = '',
  error = false,
  option_key,
  onSelect = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  disabled = false,
}: GridCellSelectProps<T, O>) => {
  const { row } = useGridRowContext<T>();

  return (
    <div className={cn.cell} style={{ padding, background, color }} onClick={() => onClick(row)}>
      <Dropdown
        placeholder={placeholder}
        left_icon={left_icon}
        background={background}
        loading={loading}
        error={error}
        onSelect={(option: O) => onSelect(option, row)}
        value={value}
        options={options}
        option_key={option_key}
        disabled={disabled}
      />
    </div>
  );
};

export const GridCellInput = <T extends Record<string, any>>({
  left_icon,
  color,
  background,
  padding = '5px',
  value = '',
  loading = false,
  placeholder,
  error = false,
  onChange = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  disabled = false,
}: GridCellInputProps<T>) => {
  const { row } = useGridRowContext<T>();

  return (
    <div className={cn.cell} style={{ padding, background, color }} onClick={() => onClick(row)}>
      <Input
        placeholder={placeholder}
        left_icon={left_icon}
        background={background}
        loading={loading}
        error={error}
        onChange={onChange}
        value={value}
        disabled={disabled}
      />
    </div>
  );
};

export const GridCellInputCurrency = <T extends Record<string, any>>({
  left_icon,
  color,
  background,
  padding = '5px',
  value = '',
  loading = false,
  placeholder,
  error = false,
  onChange = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  disabled = false,
}: GridCellInputProps<T>) => {
  const { row } = useGridRowContext<T>();

  return (
    <div className={cn.cell} style={{ padding, background, color }} onClick={() => onClick(row)}>
      <InputCurrency
        placeholder={placeholder}
        left_icon={left_icon}
        background={background}
        loading={loading}
        error={error}
        onChange={onChange}
        value={value}
        disabled={disabled}
      />
    </div>
  );
};

export const GridCellInputPercent = <T extends Record<string, any>>({
  left_icon,
  color,
  background,
  padding = '5px',
  value = '',
  loading = false,
  placeholder,
  error = false,
  onChange = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  disabled = false,
}: GridCellInputProps<T>) => {
  const { row } = useGridRowContext<T>();

  return (
    <div className={cn.cell} style={{ padding, background, color }} onClick={() => onClick(row)}>
      <InputPercent
        placeholder={placeholder}
        left_icon={left_icon}
        background={background}
        loading={loading}
        error={error}
        onChange={onChange}
        value={value}
        disabled={disabled}
      />
    </div>
  );
};

export const GridCellInputNumber = <T extends Record<string, any>>({
  left_icon,
  color,
  background,
  padding = '5px',
  value = '',
  loading = false,
  placeholder,
  error = false,
  onChange = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  disabled = false,
}: GridCellInputProps<T>) => {
  const { row } = useGridRowContext<T>();

  return (
    <div className={cn.cell} style={{ padding, background, color }} onClick={() => onClick(row)}>
      <InputNumber
        placeholder={placeholder}
        left_icon={left_icon}
        background={background}
        loading={loading}
        error={error}
        onChange={onChange}
        value={value}
        disabled={disabled}
      />
    </div>
  );
};
